import { useMemo, useState } from "react";
import "../../styles/design-system.css";
import "../features.css";
import { Tag } from "../../components/ui/Tag";
import { Badge } from "../../components/ui/Badge";
import { track } from "../../utils/analytics";
import { useFeatureView } from "../../utils/useFeatureView";

/**
 * Food-as-medicine referrals — connects trial participants to produce Rx,
 * MTM meal programs, and SNAP/WIC navigation. Eligibility filtering is
 * deterministic (income/household/diagnosis); routing is single-click for
 * the care team.
 */

const PROGRAMS = [
  {
    id: "produce-rx",
    name: "Produce Rx",
    partner: "Wholesome Wave",
    description:
      "12-week produce prescription with weekly farmers-market vouchers and dietitian touchpoints.",
    benefit: "$28 / week",
    eligible: ({ a1cAtLeast, foodInsecure }) => a1cAtLeast(7.0) && foodInsecure,
  },
  {
    id: "mtm-meals",
    name: "MTM medically-tailored meals",
    partner: "Community Servings",
    description:
      "10 prepared meals/week tailored to renal, cardiac, and diabetic clinical specs. 12-week course.",
    benefit: "10 meals / week",
    eligible: ({ hasCondition }) =>
      hasCondition("ckd") || hasCondition("heart-failure") || hasCondition("type2-diabetes"),
  },
  {
    id: "snap",
    name: "SNAP navigation",
    partner: "Project Bread",
    description:
      "On-site enrollment specialist files SNAP applications and resolves redetermination issues.",
    benefit: "Up to $291 / month / person",
    eligible: ({ incomeFPL, householdSize }) => incomeFPL <= 200 && householdSize >= 1,
  },
  {
    id: "wic",
    name: "WIC navigation",
    partner: "MA WIC Program",
    description:
      "Pregnant or postpartum patients and children under five — supplemental food and nutrition counseling.",
    benefit: "Monthly food package",
    eligible: ({ pregnantOrPostpartum, hasChildUnder5, incomeFPL }) =>
      (pregnantOrPostpartum || hasChildUnder5) && incomeFPL <= 185,
  },
];

// Maria T. (GTH-8821) — same patient threaded from trial matching → SDOH → food referrals
const DEFAULT_PATIENT = {
  name: "Maria T.",
  mrn: "GTH-8821",
  site: "Mt. Auburn FQHC",
  conditions: ["type2-diabetes", "hypertension"],
  a1c: 8.4,
  foodInsecure: true,
  incomeFPL: 130,
  householdSize: 3,
  pregnantOrPostpartum: false,
  hasChildUnder5: false,
};

function evaluatePrograms(patient) {
  const helpers = {
    a1cAtLeast: (v) => patient.a1c != null && patient.a1c >= v,
    hasCondition: (c) => patient.conditions.includes(c),
    foodInsecure: patient.foodInsecure,
    incomeFPL: patient.incomeFPL,
    householdSize: patient.householdSize,
    pregnantOrPostpartum: patient.pregnantOrPostpartum,
    hasChildUnder5: patient.hasChildUnder5,
  };
  return PROGRAMS.map((p) => ({ ...p, isEligible: p.eligible(helpers) }));
}

const ALL_CONDITIONS = [
  "type2-diabetes",
  "hypertension",
  "obesity",
  "ckd",
  "heart-failure",
];

export function FoodAsMedicine() {
  const sectionRef = useFeatureView("food_as_medicine");
  const [patient, setPatient] = useState(DEFAULT_PATIENT);
  const [referrals, setReferrals] = useState({});

  const evaluated = useMemo(() => evaluatePrograms(patient), [patient]);

  function update(key, value) {
    setPatient((prev) => ({ ...prev, [key]: value }));
  }
  function toggleCondition(c) {
    setPatient((prev) => ({
      ...prev,
      conditions: prev.conditions.includes(c)
        ? prev.conditions.filter((x) => x !== c)
        : [...prev.conditions, c],
    }));
  }
  function refer(programId) {
    track("food_referral_click", { programId });
    setReferrals((prev) => ({ ...prev, [programId]: "sent" }));
  }

  return (
    <section ref={sectionRef} className="ds-section feature-section" id="feature-food-as-medicine">
      <div className="ds-page">
        <div className="ds-section-head">
          <div className="ds-section-num">03 / 05</div>
          <div>
            <h2 className="ds-section-title">Food-as-medicine referrals</h2>
            <p className="ds-section-sub">
              Trial participants are routed to produce Rx, medically-tailored
              meal programs, and SNAP/WIC navigation. One-click referrals from
              the care team workflow.
            </p>
          </div>
        </div>

        <div className="tm-journey-strip">
          <span className="tm-journey-patient">{patient.name || "Patient"}{patient.mrn ? ` · ${patient.mrn}` : ""}{patient.site ? ` · ${patient.site}` : ""}</span>
          <span className="tm-journey-sep">·</span>
          <span className="tm-journey-step tm-journey-step--done">01 Trial matching</span>
          <span className="tm-journey-sep">→</span>
          <span className="tm-journey-step tm-journey-step--done">02 SDOH screening</span>
          <span className="tm-journey-sep">→</span>
          <span className="tm-journey-step tm-journey-step--current">03 Food referrals</span>
        </div>

        <div className="ds-grid-2 feature-spaced">
          <div className="ds-card">
            <div className="ds-card-label">
              <span>PATIENT CONTEXT</span>
              <span>SDOH + CLINICAL</span>
            </div>
            <div className="feature-form-grid">
              <label className="ds-input-label">
                A1C
                <input
                  type="number"
                  step="0.1"
                  className="ds-input"
                  value={patient.a1c}
                  onChange={(e) => update("a1c", parseFloat(e.target.value) || 0)}
                />
              </label>
              <label className="ds-input-label">
                Income (% FPL)
                <input
                  type="number"
                  className="ds-input"
                  value={patient.incomeFPL}
                  onChange={(e) => update("incomeFPL", parseInt(e.target.value, 10) || 0)}
                />
              </label>
              <label className="ds-input-label">
                Household size
                <input
                  type="number"
                  min="1"
                  className="ds-input"
                  value={patient.householdSize}
                  onChange={(e) => update("householdSize", parseInt(e.target.value, 10) || 1)}
                />
              </label>
            </div>
            <div className="feature-spaced">
              <div className="ds-input-label">Conditions</div>
              <div className="feature-pill-row">
                {ALL_CONDITIONS.map((c) => {
                  const active = patient.conditions.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      className={`feature-pill${active ? " feature-pill--active" : ""}`}
                      onClick={() => toggleCondition(c)}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="feature-spaced feature-checkboxes">
              <label>
                <input
                  type="checkbox"
                  checked={patient.foodInsecure}
                  onChange={(e) => update("foodInsecure", e.target.checked)}
                />
                Food insecure (PRAPARE positive)
                {patient.foodInsecure && patient.name === "Maria T." && (
                  <span className="fam-sdoh-source"> · confirmed via SDOH screening ✓</span>
                )}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={patient.pregnantOrPostpartum}
                  onChange={(e) => update("pregnantOrPostpartum", e.target.checked)}
                />
                Pregnant or postpartum
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={patient.hasChildUnder5}
                  onChange={(e) => update("hasChildUnder5", e.target.checked)}
                />
                Child under 5 in household
              </label>
            </div>
          </div>

          <div className="ds-card">
            <div className="ds-card-label">
              <span>WHY THESE RULES?</span>
              <span>DETERMINISTIC GATING</span>
            </div>
            <ul className="feature-referrals">
              <li><span className="feature-bullet">/</span> Produce Rx targets uncontrolled T2D with food insecurity (A1C ≥ 7).</li>
              <li><span className="feature-bullet">/</span> MTM meals serve patients with diet-modifiable conditions (CKD, HF, T2D).</li>
              <li><span className="feature-bullet">/</span> SNAP eligibility ≤ 200% FPL.</li>
              <li><span className="feature-bullet">/</span> WIC: pregnancy/postpartum or child &lt; 5, ≤ 185% FPL.</li>
            </ul>
          </div>
        </div>

        <div className="feature-spaced">
          <div className="ds-card-label">
            <span>PROGRAMS · {evaluated.filter((p) => p.isEligible).length} ELIGIBLE</span>
            <span>ONE-CLICK REFER</span>
          </div>
          <div className="ds-grid-2">
            {evaluated.map((p) => {
              const sent = referrals[p.id] === "sent";
              return (
                <div key={p.id} className="ds-card">
                  <div className="feature-program-head">
                    <h3 className="feature-program-name">{p.name}</h3>
                    <Tag variant={p.isEligible ? "slash" : "default"}>
                      {p.isEligible ? "/ eligible" : "ineligible"}
                    </Tag>
                  </div>
                  <div className="feature-program-meta">
                    {p.partner} · {p.benefit}
                  </div>
                  <p className="feature-program-desc">{p.description}</p>
                  <div className="feature-actions">
                    <button
                      type="button"
                      className="ds-btn ds-btn--slash"
                      disabled={!p.isEligible || sent}
                      onClick={() => refer(p.id)}
                    >
                      {sent ? "Referral sent" : "Refer patient"}
                    </button>
                    {sent && <Badge status="live">Queued</Badge>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default FoodAsMedicine;
